import React, { useEffect, useState } from 'react';
import { marketing } from '../services/api';
import { toast } from '../hooks/use-toast';
import { Bot, Loader2, Save, ChevronDown, User, ChevronLeft } from 'lucide-react';

// Admin — Auto-Responder. Two modes:
// 1) Platform mode (default): edit the platform-wide default templates that
//    apply to every owner who hasn't overridden the trigger.
// 2) Owner mode: pick a specific business owner and directly edit / reset
//    their overrides (spec: "Admin dashboard shows all business owners'
//    auto-responder sequences with full edit and override access").
export default function AdminAutoResponder() {
  const [rows, setRows] = useState(null);
  const [owners, setOwners] = useState([]);
  const [openKey, setOpenKey] = useState(null);
  const [scopeOwner, setScopeOwner] = useState(null);  // null = platform

  const load = async () => {
    setOpenKey(null);
    try {
      if (scopeOwner) {
        const tpls = await marketing.listAutoResponderTemplates(null, scopeOwner.ownerId);
        setRows(tpls || []);
      } else {
        const [tpls, os] = await Promise.all([
          marketing.adminListAutoResponderTemplates(),
          marketing.adminListAutoResponderOwners(),
        ]);
        setRows(tpls || []); setOwners(os || []);
      }
    } catch (e) {
      toast({ title: 'Load failed', description: e?.response?.data?.detail || '' });
      setRows([]);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scopeOwner?.ownerId]);

  const toggle = async (row) => {
    const next = !row.enabled;
    setRows(rows.map(r => r.key === row.key ? { ...r, enabled: next } : r));
    try {
      if (scopeOwner) await marketing.toggleAutoResponder(row.key, next, scopeOwner.ownerId);
      else await marketing.adminToggleAutoResponder(row.key, next);
    } catch (e) { setRows(rows); toast({ title: 'Toggle failed' }); }
  };

  if (rows === null) return <div className="text-gray-400 inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin"/> Loading…</div>;

  // OWNER MODE — drill-down
  if (scopeOwner) {
    return (
      <div className="space-y-4" data-testid="admin-ar-owner-scope-page">
        <button onClick={() => setScopeOwner(null)} data-testid="admin-ar-back-to-platform"
                className="text-sm text-gray-400 hover:text-white inline-flex items-center gap-1">
          <ChevronLeft size={14}/> Back to platform defaults
        </button>
        <div>
          <div className="text-xs text-yellow-400 uppercase tracking-wide">Editing owner overrides</div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <User size={20}/> {scopeOwner.ownerName || scopeOwner.email}
          </h1>
          <p className="text-sm text-gray-400 mt-1">Business: {scopeOwner.businessName || '—'}. Changes here override this owner's templates only. Deleting a row falls back to the platform default.</p>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-lg divide-y divide-gray-800">
          {rows.map(row => (
            <AdminRow key={row.key} row={row} isOpen={openKey === row.key}
                       onToggleOpen={() => setOpenKey(openKey === row.key ? null : row.key)}
                       onToggleEnabled={() => toggle(row)}
                       onSaved={(fresh) => setRows(rows.map(r => r.key === row.key ? { ...r, ...fresh } : r))}
                       ownerScopeId={scopeOwner.ownerId}/>
          ))}
        </div>
      </div>
    );
  }

  // PLATFORM MODE — default
  return (
    <div className="space-y-4" data-testid="admin-auto-responder-page">
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2"><Bot size={22}/> Auto-Responder — Platform</h1>
        <p className="text-sm text-gray-400 mt-1">These defaults apply to every business owner who hasn't overridden the trigger. Owner-level overrides always win.</p>
      </div>

      <div className="bg-gray-900 border border-gray-800 rounded-lg divide-y divide-gray-800">
        {rows.map(row => (
          <AdminRow key={row.key} row={row} isOpen={openKey === row.key}
                     onToggleOpen={() => setOpenKey(openKey === row.key ? null : row.key)}
                     onToggleEnabled={() => toggle(row)}
                     onSaved={(fresh) => setRows(rows.map(r => r.key === row.key ? { ...r, ...fresh } : r))}
                     ownerScopeId={null}/>
        ))}
      </div>

      {/* Owner snapshot with drill-in */}
      <div>
        <h2 className="text-white font-semibold mt-6 mb-2">Business owners — auto-responder status</h2>
        <div className="bg-gray-900 border border-gray-800 rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-gray-400 uppercase tracking-wide">
              <tr>
                <th className="text-left px-3 py-2">Owner</th>
                <th className="text-left px-3 py-2">Business</th>
                <th className="text-right px-3 py-2">Custom Templates</th>
                <th className="text-left px-3 py-2">Disabled Triggers</th>
                <th className="text-right px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="text-gray-200">
              {owners.map(o => (
                <tr key={o.ownerId} className="border-t border-gray-800" data-testid={`admin-ar-owner-${o.ownerId}`}>
                  <td className="px-3 py-2">{o.ownerName || '—'}<div className="text-xs text-gray-500">{o.email}</div></td>
                  <td className="px-3 py-2">{o.businessName || <span className="text-gray-500">—</span>}</td>
                  <td className="px-3 py-2 text-right">{o.overriddenTemplates || 0}</td>
                  <td className="px-3 py-2 text-xs text-gray-400">
                    {o.disabledTriggers?.length ? o.disabledTriggers.join(', ') : <span className="text-gray-600">none</span>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button data-testid={`admin-ar-edit-owner-${o.ownerId}`} onClick={() => setScopeOwner(o)}
                            className="text-xs pbb-btn-green">Edit overrides</button>
                  </td>
                </tr>
              ))}
              {!owners.length && <tr><td colSpan={5} className="px-3 py-6 text-center text-gray-500">No business owners found.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function AdminRow({ row, isOpen, onToggleOpen, onToggleEnabled, onSaved, ownerScopeId }) {
  const [form, setForm] = useState({ subject: row.subject, emailBody: row.emailBody, smsBody: row.smsBody });
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm({ subject: row.subject, emailBody: row.emailBody, smsBody: row.smsBody }); }, [row.subject, row.emailBody, row.smsBody]);

  const save = async () => {
    setSaving(true);
    try {
      const r = ownerScopeId
        ? await marketing.putAutoResponderTemplate(row.key, form, ownerScopeId)
        : await marketing.adminPutAutoResponderTemplate(row.key, form);
      onSaved(r);
      toast({ title: ownerScopeId ? `Owner override saved: "${row.label}"` : `Platform default updated: "${row.label}"` });
    } catch (e) { toast({ title: 'Save failed', description: e?.response?.data?.detail || '' }); }
    finally { setSaving(false); }
  };
  const reset = async () => {
    if (!ownerScopeId) return;
    if (!window.confirm(`Delete this owner's override for "${row.label}" and fall back to platform default?`)) return;
    try {
      const r = await marketing.deleteAutoResponderTemplate(row.key, ownerScopeId);
      onSaved(r);
      toast({ title: 'Reverted to platform default' });
    } catch (e) { toast({ title: 'Reset failed' }); }
  };

  return (
    <div className="p-3" data-testid={`admin-ar-row-${row.key}`}>
      <div className="flex items-center gap-3">
        <button onClick={onToggleOpen} className="flex-1 min-w-0 flex items-center gap-2 text-left">
          <ChevronDown size={14} className={`text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}/>
          <div className="min-w-0">
            <div className="text-white text-sm font-medium truncate">{row.label}</div>
            <div className="text-xs text-gray-500 truncate">{row.subject || '(no subject)'}</div>
          </div>
        </button>
        <label className="inline-flex items-center gap-1 text-xs cursor-pointer">
          <input data-testid={`admin-ar-toggle-${row.key}`} type="checkbox" checked={row.enabled} onChange={onToggleEnabled}/>
          <span className={row.enabled ? 'text-green-400' : 'text-gray-500'}>{row.enabled ? 'On' : 'Off'}</span>
        </label>
      </div>
      {isOpen && (
        <div className="mt-3 space-y-2 pl-6">
          <input value={form.subject || ''} onChange={e => setForm({ ...form, subject: e.target.value })}
                 data-testid={`admin-ar-subject-${row.key}`}
                 className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white text-sm"/>
          <textarea rows={5} value={form.emailBody || ''} onChange={e => setForm({ ...form, emailBody: e.target.value })}
                    data-testid={`admin-ar-emailBody-${row.key}`}
                    className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white text-sm font-mono"/>
          <textarea rows={2} maxLength={240} value={form.smsBody || ''} onChange={e => setForm({ ...form, smsBody: e.target.value })}
                    data-testid={`admin-ar-smsBody-${row.key}`}
                    className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white text-sm"/>
          <div className="flex gap-2 flex-wrap">
            <button data-testid={`admin-ar-save-${row.key}`} onClick={save} disabled={saving}
                    className="pbb-btn-green inline-flex items-center gap-1 text-xs disabled:opacity-60">
              {saving ? <Loader2 size={12} className="animate-spin"/> : <Save size={12}/>} {ownerScopeId ? 'Save owner override' : 'Save platform default'}
            </button>
            {ownerScopeId && (
              <button data-testid={`admin-ar-reset-${row.key}`} onClick={reset}
                      className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded border border-gray-700 text-white hover:bg-gray-800">
                Revert to platform default
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
